/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useState, useMemo, useEffect, useRef
} from 'react';
import {
  string, number, oneOfType, bool, func, shape, arrayOf
} from 'prop-types';
import { useCabinetVisualizerStore } from '../../../../store';
import {
  selectVizImageRoute,
} from '../../../../store/cabinetVisualizer/selectors';
import { CabinetShape } from './type';
import { useCabinetStore } from '../../../../store/ui';
import {
  getCabinetStyle,
  getImageWapperClass,
  getSpacerTextStyle,
  handleImageError,
  handleImageLoad,
  handleMouseDown,
  handleMouseUp,
  handleSpacerImageError
} from './utils/wall-designer-cabinet-utils';
import { useImageState } from '../../../../hooks/Cabinets/useImageState';
import { useIsMobileDevice } from '../../../../hooks/useIsMobileDevice';
import { MoveIconOverlay } from './MoveIconOverlay';
import { CABINET_SKU, BASE_BLINDS_MEDIA_URL } from '../../cabinetConstants';

export const Cabinet = ({
  id,
  left, top, width, height, itemId, mirror,
  designId, origin, spacer, spacerName, spacerMediaUrl,
  cabinets,
  dragListeners,
  dragAttributes,
  border,
  onCabinetRectChange
}) => {
  const isSmallWidth = useMemo(() => width < 40, [width]);
  const cabinetRef = useRef(null);
  const { cabinetVisualizer } = useCabinetVisualizerStore();
  const { styleChoiceId } = useCabinetVisualizerStore((state) => state.cabinetVisualizer);
  const selectedCabinet = useCabinetStore((state) => state.selectedCabinet);
  const setSelectedCabinet = useCabinetStore((state) => state.setSelectedCabinet);
  const [mouseDownPos, setMouseDownPos] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [spacerImageFailed, setSpacerImageFailed] = useState(false);
  const isMobile = useIsMobileDevice();
  const { imageState, setImageState } = useImageState({ itemId });

  useEffect(() => {
    if (cabinetRef.current && onCabinetRectChange) {
      onCabinetRectChange(cabinetRef.current);
    }
  });

  const vizImageRoute = useMemo(
    () => {
      // Use the destructured styleChoiceId to ensure we have the latest value
      const stateWithLatestStyleChoiceId = { ...cabinetVisualizer, styleChoiceId };
      return selectVizImageRoute(stateWithLatestStyleChoiceId, itemId);
    },
    [styleChoiceId, cabinetVisualizer.choices, itemId]
  );
  const showMoveIcon = !isMobile && isHovered && !selectedCabinet && itemId !== CABINET_SKU.VOID_SPACE;

  // Treat base URL as invalid - custom spacers may have this as their mediaUrl
  // Also fall back to text if the spacer image failed to load
  const validSpacerMediaUrl = spacerMediaUrl
    && spacerMediaUrl !== BASE_BLINDS_MEDIA_URL
    && !spacerImageFailed
    ? spacerMediaUrl
    : null;

  return (
    <>
      <div
        className={`cabinet cabinet-${imageState} ${showMoveIcon ? '!sui-overflow-visible' : ''}`}
        style={getCabinetStyle({
          left, top, width, height, imageState, mirror, border
        })}
        ref={cabinetRef}
      >
        <div role="button" tabIndex={0} className={showMoveIcon ? '!sui-overflow-visible' : ''}>
          <div
            role="button"
            tabIndex={0}
            className={`${getImageWapperClass({ selectedCabinet, id })} ${showMoveIcon ? '!sui-overflow-visible' : ''}`}
            onMouseDown={(event) => handleMouseDown({
              event, cabinets, id, setSelectedCabinet, setMouseDownPos
            })}
            onMouseUp={(event) => handleMouseUp({
              event, mouseDownPos, setMouseDownPos, cabinets, id, setSelectedCabinet
            })}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            {...dragListeners}
            {...dragAttributes}
          >
            {(imageState !== 'failed' && imageState !== 'smallWidth' && spacer) && (
              <><img
                src={vizImageRoute}
                draggable={false}
                alt="cabinet"
                width="auto"
                height="auto"
                onLoad={() => handleImageLoad({ setImageState })}
                onError={() => handleImageError({
                  setImageState, isSmallWidth, itemId, designId, styleChoiceId, vizImageRoute
                })}
              /><MoveIconOverlay isVisible={showMoveIcon} />
              </>
            )}
          </div>
          {!spacer && (
            <>
              <div
                role="button"
                tabIndex={0}
                className="sui-flex sui-justify-center sui-items-center sui-text-base sui-relative sui-overflow-hidden"
                style={{
                  height,
                  width,
                  backgroundColor: validSpacerMediaUrl ? 'transparent' : 'gray',
                  color: '#FFF',
                  margin: '1px'
                }}
                onMouseDown={(event) => handleMouseDown({
                  event, cabinets, id, setSelectedCabinet, setMouseDownPos
                })}
                onMouseUp={(event) => handleMouseUp({
                  event, mouseDownPos, setMouseDownPos, cabinets, id, setSelectedCabinet
                })}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                {...dragListeners}
                {...dragAttributes}
              >
                {validSpacerMediaUrl ? (
                  <img
                    src={validSpacerMediaUrl}
                    draggable={false}
                    alt={spacerName || 'spacer'}
                    width={width}
                    height={height}
                    onError={() => handleSpacerImageError({
                      spacerMediaUrl, spacerName, designId, setSpacerImageFailed
                    })}
                  />
                ) : (
                  <b style={getSpacerTextStyle({ origin, spacerName, mirror })}>{spacerName}</b>
                )}
              </div>
              <MoveIconOverlay isVisible={showMoveIcon} />
            </>
          )}
        </div>
      </div>
    </>
  );
};

Cabinet.propTypes = {
  id: string,
  left: oneOfType([string, number]),
  top: oneOfType([string, number]),
  width: oneOfType([string, number]),
  height: oneOfType([string, number]),
  itemId: oneOfType([string, number]).isRequired,
  mirror: bool,
  handleQuickView: func.isRequired,
  handleDuplicate: func.isRequired,
  designId: string.isRequired,
  origin: shape({
    Row: number.isRequired,
    Column: number.isRequired,
    Height: number.isRequired,
    Width: number.isRequired,
  }).isRequired,
  spacer: bool,
  spacerName: string,
  spacerMediaUrl: string,
  cabinets: arrayOf(CabinetShape),
  topCabinets: arrayOf(CabinetShape),
  bottomCabinets: arrayOf(CabinetShape),
  anchorSide: string,
  onEditCabinetAnalytics: func.isRequired,
  dragListeners: shape({}),
  dragAttributes: shape({}),
  border: bool,
  onCabinetRectChange: func,
};

Cabinet.defaultProps = {
  id: '',
  left: 0,
  top: 0,
  width: 0,
  height: 0,
  mirror: false,
  spacer: true,
  spacerName: '',
  spacerMediaUrl: '',
  cabinets: [],
  topCabinets: [],
  bottomCabinets: [],
  anchorSide: 'left',
  dragListeners: {},
  dragAttributes: {},
  border: false,
  onCabinetRectChange: undefined
};
